import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import StatusPill from '../components/StatusPill';

const autonomy = [
  ['A0', 'Observa', 'Sin automatización agentica.'],
  ['A1', 'Asiste', 'Analiza y sugiere; un humano aprueba.'],
  ['A2', 'Delegación controlada', 'Actúa solo con aprobación previa.'],
  ['A3', 'Opera con políticas', 'Ejecuta bajo límites preaprobados.'],
  ['A4', 'Autonomía acotada', 'Opera con supervisión en tiempo real.'],
];

function Governance() {
  const { apiUrl, overview, userId, currentUser } = useOutletContext();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Business Context state
  const [bizCtx, setBizCtx] = useState(null);
  const [bizLoading, setBizLoading] = useState(true);
  const [rawTextDraft, setRawTextDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bizMessage, setBizMessage] = useState(null);

  const isAdmin = Boolean(currentUser?.can_admin);

  useEffect(() => {
    // user_id is optional (see routers/agents.py) -- only needed so the
    // two CI-based agents (forge-spec-review, claude-action) can report
    // real GitHub Actions activity; the rest of the catalog works without it.
    const url = userId ? `${apiUrl}/api/agents/cards?user_id=${userId}` : `${apiUrl}/api/agents/cards`;
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (res.ok) {
          setCards(await res.json());
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiUrl, userId]);

  // Fetch business context on mount (all authenticated users)
  useEffect(() => {
    if (!userId) {
      setBizLoading(false);
      return;
    }
    let cancelled = false;
    setBizLoading(true);
    fetch(`${apiUrl}/api/business-context?user_id=${userId}`, { credentials: 'include' })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            setBizCtx(data);
            setRawTextDraft(data.raw_text || '');
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBizLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, userId]);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    setBizMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/business-context?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw_text: rawTextDraft }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setBizMessage({ type: 'error', text: data.detail || 'No se pudo guardar.' });
        return;
      }
      // Update raw_text in state but preserve existing ai_description
      setBizCtx((prev) => ({
        ...prev,
        ...data,
        ai_description: prev?.ai_description ?? data.ai_description,
        last_generated_at: prev?.last_generated_at ?? data.last_generated_at,
      }));
      setBizMessage({ type: 'success', text: 'Contexto guardado.' });
    } catch {
      setBizMessage({ type: 'error', text: 'No se pudo conectar con el API.' });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!userId) return;
    setGenerating(true);
    setBizMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/business-context/generate?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setBizMessage({ type: 'error', text: data.detail || 'No se pudo generar.' });
        return;
      }
      setBizCtx((prev) => ({
        ...prev,
        ai_description: data.ai_description,
        last_generated_at: data.last_generated_at,
      }));
      setBizMessage({ type: 'success', text: 'Descripción generada.' });
    } catch {
      setBizMessage({ type: 'error', text: 'No se pudo conectar con el API.' });
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="forge-page">
      <div className="forge-page-header">
        <div>
          <div className="forge-section-eyebrow">Módulo 02 · Gobierno</div>
          <h1 className="forge-title">Gobernar</h1>
          <p className="forge-lead">
            Semáforo, niveles de autonomía y Agent Cards como evidencia viva de quién
            puede hacer qué, con qué límites y bajo qué revisión.
          </p>
        </div>
      </div>

      <div className="forge-metric-row">
        <div className="forge-metric"><div className="label">verde</div><div className="value">{overview?.sem_summary?.green ?? 0}</div></div>
        <div className="forge-metric"><div className="label">amarillo</div><div className="value">{overview?.sem_summary?.amber ?? 0}</div></div>
        <div className="forge-metric"><div className="label">rojo</div><div className="value">{overview?.sem_summary?.red ?? 0}</div></div>
        <div className="forge-metric"><div className="label">agentes</div><div className="value">{cards.length || overview?.active_agents || 0}</div></div>
      </div>

      <h2 className="forge-section-title"><span className="num">A.</span> Autonomía A0-A4</h2>
      <div className="forge-list">
        {autonomy.map(([level, title, detail]) => {
          // Dynamic, not hardcoded: a level counts as "activo" only if the
          // real Agent Cards catalog has at least one card at that level
          // with status "active" (implemented AND has actually run).
          const isActive = cards.some((c) => c.level === level && c.status === 'active');
          return (
            <div key={level} className="forge-row">
              <span className="forge-kbd">{level}</span>
              <div className="forge-row-main">
                <div className="forge-row-title">{title}</div>
                <div className="forge-row-detail">{detail}</div>
              </div>
              <StatusPill color={isActive ? 'green' : 'steel'}>{isActive ? 'activo' : 'sin actividad'}</StatusPill>
            </div>
          );
        })}
      </div>

      <h2 className="forge-section-title"><span className="num">B.</span> Catálogo de Agent Cards</h2>
      {loading ? (
        <div className="forge-empty">Loading agent cards…</div>
      ) : (
        <div className="forge-grid two">
          {cards.map((card) => (
            <div key={card.agent_id} className="forge-panel">
              <StatusPill color={card.status === 'active' ? 'green' : card.status === 'inactive' ? 'steel' : 'amber'}>
                {card.status}
              </StatusPill>
              {card.review_overdue && (
                <span style={{ marginLeft: '0.4rem' }}>
                  <StatusPill color="red">review vencido</StatusPill>
                </span>
              )}
              <h2>{card.name}</h2>
              <p className="forge-row-detail">{card.agent_id}</p>
              <div className="forge-row-detail">Nivel {card.level} · tools: {card.tools.join(', ') || 'none'}</div>
              <p className="forge-row-detail" style={{ marginTop: '0.3rem' }}>{card.description}</p>
              {card.total_calls > 0 && (
                <div className="forge-row-detail" style={{ marginTop: '0.3rem', fontSize: '0.75rem' }}>
                  {card.total_calls} calls ({card.successful_calls} ok, {card.failed_calls} fail)
                  {card.last_active && ` · last: ${new Date(card.last_active).toLocaleDateString()}`}
                </div>
              )}
              {card.review_date && (
                <div className="forge-row-detail" style={{ marginTop: '0.3rem', fontSize: '0.75rem' }}>
                  review_date: {card.review_date}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Section C: Business Context ── */}
      <h2 className="forge-section-title"><span className="num">C.</span> Contexto de Negocio</h2>

      {bizLoading ? (
        <div className="forge-empty">Cargando contexto…</div>
      ) : (
        <div className="forge-panel biz-ctx-panel">

          {/* Read-only raw_text display (non-admins only; admins edit it below) */}
          {!isAdmin && (
            <div className="biz-ctx-block">
              <div className="biz-ctx-label">Descripción del negocio</div>
              <div className="biz-ctx-readonly">
                {bizCtx?.raw_text
                  ? bizCtx.raw_text
                  : <span className="biz-ctx-placeholder">Sin descripción aún</span>}
              </div>
            </div>
          )}

          {/* Admin-only editing area */}
          {isAdmin && (
            <div className="biz-ctx-admin">
              <div className="biz-ctx-block">
                <label className="biz-ctx-label" htmlFor="biz-ctx-textarea">
                  Descripción del negocio
                </label>
                <textarea
                  id="biz-ctx-textarea"
                  className="biz-ctx-textarea"
                  value={rawTextDraft}
                  onChange={(e) => setRawTextDraft(e.target.value)}
                  rows={6}
                  placeholder="Describe el negocio, su misión, contexto y objetivos…"
                />
              </div>

              <div className="biz-ctx-helper">
                Guarda los cambios antes de generar
              </div>

              <div className="biz-ctx-actions">
                <button
                  type="button"
                  className="forge-button primary"
                  onClick={handleSave}
                  disabled={saving || generating}
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
                <button
                  type="button"
                  className="forge-button"
                  onClick={handleGenerate}
                  disabled={generating || saving}
                >
                  {generating ? 'Generando…' : 'Generar descripción con IA'}
                </button>
              </div>

              {bizMessage && (
                <div className={`biz-ctx-message ${bizMessage.type}`}>
                  {bizMessage.text}
                </div>
              )}
            </div>
          )}

          {/* Read-only ai_description display (all users), below the editor */}
          <div className="biz-ctx-block">
            <div className="biz-ctx-label">
              Descripción generada por IA
              {bizCtx?.last_generated_at && (
                <span className="biz-ctx-date">
                  · Generada el {formatDate(bizCtx.last_generated_at)}
                </span>
              )}
            </div>
            <div className="biz-ctx-readonly biz-ctx-ai">
              {bizCtx?.ai_description
                ? bizCtx.ai_description
                : <span className="biz-ctx-placeholder">Sin descripción de IA aún</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Governance;
